// components/plScenario/AdjustmentPropagationModal.jsx
import React, { useState, useEffect } from 'react';
import { CheckCircleIcon, ArrowLeftIcon } from '@heroicons/react/24/outline';
import Modal from '../../../common/Modal';
import { getButtonClass, getAlertClass, MODAL_THEME } from '../../../../config/theme.config';

export const AdjustmentPropagationModal = ({ 
  adjustment, 
  onChoice, 
  onCancel,
  months,
  selectedYear,
  availableYears 
}) => {
  const [mode, setMode] = useState('propagation'); // 'propagation' or 'bulk-edit'
  const [bulkValues, setBulkValues] = useState({});

  // Initialize bulk values when switching to bulk edit mode
  useEffect(() => {
    if (mode === 'bulk-edit' && adjustment) {
      const initialValues = {};
      months.forEach(month => {
        initialValues[month.month_num] = adjustment.currentValues?.[month.month_num] || 0;
      });
      initialValues[adjustment.monthNum] = adjustment.value;
      setBulkValues(initialValues);
    }
  }, [mode, adjustment, months]);

  if (!adjustment) return null;

  const { accountId, monthNum, value, year } = adjustment;
  
  const month = months.find(m => m.month_num === monthNum);
  const monthName = month ? month.month_abbr : `Month ${monthNum}`;
  
  const futureMonthsInYear = months.filter(m => m.month_num >= monthNum && !m.is_actual).length;
  const futureYears = availableYears.filter(y => y > year);
  const futureYearsCount = futureYears.length;
  const totalFutureMonths = futureMonthsInYear + (futureYearsCount * 12);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(amount);
  };

  const handleBulkSave = () => {
    onChoice('bulk', bulkValues);
    setMode('propagation');
  };

  const handleBack = () => {
    setMode('propagation');
    setBulkValues({});
  };

  const handleBulkChange = (monthNum, value) => {
    setBulkValues(prev => ({
      ...prev,
      [monthNum]: parseFloat(value) || 0
    }));
  };

  // Group months by quarter
  const quarters = [
    { label: 'Q1', months: months.filter(m => [1, 2, 3].includes(m.month_num)) },
    { label: 'Q2', months: months.filter(m => [4, 5, 6].includes(m.month_num)) },
    { label: 'Q3', months: months.filter(m => [7, 8, 9].includes(m.month_num)) },
    { label: 'Q4', months: months.filter(m => [10, 11, 12].includes(m.month_num)) },
  ];

  // Custom title with back button for bulk-edit mode
  const titleContent = mode === 'bulk-edit' ? (
    <div className="flex items-center gap-3">
      <button
        onClick={handleBack}
        className="text-white/80 hover:text-white transition-colors -ml-2"
      >
        <ArrowLeftIcon className="w-5 h-5" />
      </button>
      <span>Edit All Months</span>
    </div>
  ) : (
    'Apply Adjustment'
  );

  // Footer content based on mode
  const footer = mode === 'bulk-edit' ? (
    <>
      <button
        onClick={handleBack}
        className={getButtonClass('secondary')}
      >
        Back
      </button>
      <button
        onClick={handleBulkSave}
        className={getButtonClass('primary')}
      >
        Save All Changes
      </button>
    </>
  ) : (
    <button
      onClick={onCancel}
      className={getButtonClass('secondary')}
    >
      Cancel
    </button>
  );

  return (
    <Modal
      isOpen={!!adjustment}
      onClose={onCancel}
      title={titleContent}
      footer={footer}
      size="md"
      useGradientHeader={true}
    >
      {mode === 'propagation' ? (
        // ============ PROPAGATION OPTIONS VIEW ============
        <div className="space-y-4">
          {/* Summary */}
          <div className={getAlertClass('info')}>
            <p>
              You're changing <span className={MODAL_THEME.text.heading}>{monthName} {year}</span> to{' '}
              <span className={MODAL_THEME.text.emphasis}>{formatCurrency(value)}</span>
            </p>
          </div>

          {/* Options */}
          <div className="space-y-3">
            <p className={`text-sm ${MODAL_THEME.text.heading}`}>
              How should this adjustment be applied?
            </p>
            
            {/* Option 1: Single Month */}
            <button
              onClick={() => onChoice('single')}
              className="w-full text-left p-4 border-2 border-grey-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-all group"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full border-2 border-grey-300 group-hover:border-brand-500 group-hover:bg-brand-500 flex items-center justify-center transition-all">
                    <CheckCircleIcon className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className={MODAL_THEME.text.heading}>This month only</h3>
                  <p className={`text-sm ${MODAL_THEME.text.muted} mt-1`}>
                    Apply to <span className="font-medium">{monthName} {year}</span> only
                  </p>
                </div>
              </div>
            </button>

            {/* Option 2: Future Months in Year */}
            {futureMonthsInYear > 1 && (
              <button
                onClick={() => onChoice('future-months')}
                className="w-full text-left p-4 border-2 border-grey-200 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-all group"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full border-2 border-grey-300 group-hover:border-brand-500 group-hover:bg-brand-500 flex items-center justify-center transition-all">
                      <CheckCircleIcon className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={MODAL_THEME.text.heading}>Rest of {year}</h3>
                    <p className={`text-sm ${MODAL_THEME.text.muted} mt-1`}>
                      Apply to <span className="font-medium">{monthName}</span> through December {year}{' '}
                      <span className={MODAL_THEME.text.emphasis}>({futureMonthsInYear} months)</span>
                    </p>
                  </div>
                </div>
              </button>
            )}

            {/* Option 3: All Future Years */}
            {futureYearsCount > 0 && (
              <button
                onClick={() => onChoice('future-years')}
                className="w-full text-left p-4 border-2 border-brand-300 rounded-lg hover:border-brand-500 hover:bg-brand-50 transition-all group bg-brand-25"
              >
                <div className="flex items-start gap-3">
                  <div className="flex-shrink-0 mt-0.5">
                    <div className="w-5 h-5 rounded-full border-2 border-brand-400 group-hover:border-brand-500 group-hover:bg-brand-500 flex items-center justify-center transition-all">
                      <CheckCircleIcon className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                    </div>
                  </div>
                  <div className="flex-1">
                    <h3 className={MODAL_THEME.text.heading}>All future months & years</h3>
                    <p className={`text-sm ${MODAL_THEME.text.muted} mt-1`}>
                      Apply to <span className="font-medium">{monthName} {year}</span> and beyond{' '}
                      <span className={MODAL_THEME.text.emphasis}>
                        ({totalFutureMonths} months across {year}
                        {futureYears.length > 0 && `, ${futureYears.join(', ')}`})
                      </span>
                    </p>
                    <div className="mt-2 bg-brand-100 border border-brand-200 rounded px-2 py-1 inline-block">
                      <p className="text-xs text-brand-800 font-medium">
                        ⚡ Recommended for consistent changes
                      </p>
                    </div>
                  </div>
                </div>
              </button>
            )}

            {/* Option 4: Bulk Edit */}
            <button
              onClick={() => setMode('bulk-edit')}
              className="w-full text-left p-4 border-2 border-violet-200 rounded-lg hover:border-violet-500 hover:bg-violet-50 transition-all group bg-violet-25"
            >
              <div className="flex items-start gap-3">
                <div className="flex-shrink-0 mt-0.5">
                  <div className="w-5 h-5 rounded-full border-2 border-violet-300 group-hover:border-violet-500 group-hover:bg-violet-500 flex items-center justify-center transition-all">
                    <CheckCircleIcon className="w-3 h-3 text-white opacity-0 group-hover:opacity-100 transition-opacity" />
                  </div>
                </div>
                <div className="flex-1">
                  <h3 className={MODAL_THEME.text.heading}>Edit each month individually</h3>
                  <p className={`text-sm ${MODAL_THEME.text.muted} mt-1`}>
                    Customize values for each month in {year}
                  </p>
                  <div className="mt-2 bg-violet-100 border border-violet-200 rounded px-2 py-1 inline-block">
                    <p className="text-xs text-violet-800 font-medium">
                      ✏️ Best for varying monthly budgets
                    </p>
                  </div>
                </div>
              </div>
            </button>
          </div>
        </div>
      ) : (
        // ============ BULK EDIT VIEW ============
        <div>
          <div className="mb-4 bg-violet-50 border border-violet-200 rounded-lg p-4">
            <p className={`text-sm ${MODAL_THEME.text.body}`}>
              Editing all months for <span className={MODAL_THEME.text.heading}>{year}</span>
              <span className="ml-2 text-violet-600 text-xs font-medium">
                (Actual months are read-only)
              </span>
            </p>
          </div>

          <div className="space-y-6">
            {quarters.map(quarter => (
              <div key={quarter.label}>
                <h4 className={`text-sm ${MODAL_THEME.text.heading} mb-3 border-b border-grey-200 pb-1`}>
                  {quarter.label}
                </h4>
                <div className="grid grid-cols-3 gap-3">
                  {quarter.months.map(m => {
                    const isActual = m.is_actual;
                    const isEdited = m.month_num === monthNum;
                    
                    return (
                      <div key={m.month_num} className="flex flex-col">
                        <label className={`text-xs font-medium ${MODAL_THEME.text.muted} mb-1`}>
                          {m.month_abbr}
                          {isActual && (
                            <span className={`ml-1 ${MODAL_THEME.text.light} text-[10px]`}>(Actual)</span>
                          )}
                          {isEdited && !isActual && (
                            <span className="ml-1 text-yellow-600 text-[10px]">✓ Just edited</span>
                          )}
                        </label>
                        <input
                          type="number"
                          value={bulkValues[m.month_num] || 0}
                          onChange={(e) => handleBulkChange(m.month_num, e.target.value)}
                          disabled={isActual}
                          className={`px-3 py-2 border rounded-lg text-sm text-right transition-all ${
                            isActual
                              ? 'bg-grey-100 text-grey-500 cursor-not-allowed border-grey-200'
                              : isEdited
                              ? 'border-yellow-400 bg-yellow-50 ring-2 ring-yellow-200'
                              : 'border-grey-300 focus:border-brand-500 focus:ring-2 focus:ring-brand-200'
                          }`}
                        />
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </Modal>
  );
};